import React from 'react';

import { Link } from 'react-router-dom';

import type { BackendTypes } from '@tf/api';
import type { TFEmptyStateMessageProps } from '@tf/ui';
import {
	Button,
	Center,
	createStyles,
	Flex,
	Icon,
	TFCard,
	TFDataTable,
	TFEmptyStateMessage,
	TFLoadingOverlay,
	TFText,
	Tooltip,
} from '@tf/ui';

import { useRevokeUserInvitationMutation, useUsersInvitations } from '@/core/api/users';
import { capabilities, capabilitiesIcons, getDisplayName } from '@/core/utils';

const useStyles = createStyles(({ colors }) => ({
	icon: {
		color: colors.gray[6],
	},
}));

export const UserInvitationsTable = () => {
	const { classes } = useStyles();
	const { isPending, isError, data: usersInvitations } = useUsersInvitations();
	const revokeUserInvitationMutation = useRevokeUserInvitationMutation();

	if (isPending) {
		return <TFLoadingOverlay h={300} label="Invitations loading..." />;
	}

	const errorMessage: TFEmptyStateMessageProps = {
		icon: <Icon.IconUnlink />,
		title: 'Error',
		description: 'Please try again',
	};

	if (isError) {
		return (
			<TFCard>
				<Center h={300}>
					<TFEmptyStateMessage {...errorMessage} />
				</Center>
			</TFCard>
		);
	}

	return (
		<TFDataTable<BackendTypes.UserInvitation>
			noDataMessage={{
				icon: <Icon.IconUserOff />,
				title: 'No invitations found',
				primaryAction: (
					<Button
						component={Link}
						to="/settings/invitations/invite"
						size="md"
						leftSection={<Icon.IconPlus size={18} stroke={2.25} />}
					>
						Invite users
					</Button>
				),
			}}
			name="inviations-table"
			data={usersInvitations}
			defs={[
				{
					header: 'Name',
					accessorKey: 'name',
					accessorFn: (user) =>
						getDisplayName({ firstName: user.firstName, lastName: user.lastName, username: 'N/A' }),
					size: 150,
					sortingFn: (rowA, rowB) => {
						const rowAName = getDisplayName({
							firstName: rowA.original.firstName,
							lastName: rowA.original.lastName,
							username: 'N/A',
						});
						const rowBName = getDisplayName({
							firstName: rowB.original.firstName,
							lastName: rowB.original.lastName,
							username: 'N/A',
						});
						return rowAName.localeCompare(rowBName);
					},
					Cell: ({ row }: { row: { original: BackendTypes.UserInvitation } }) => {
						return (
							<TFText inherit py=".25rem" lineClamp={1}>
								{getDisplayName({
									firstName: row.original.firstName,
									lastName: row.original.lastName,
									username: 'N/A',
								})}
							</TFText>
						);
					},
				},
				{
					header: 'Email',
					accessorKey: 'email',
					size: 100,
					Cell: ({ row }: { row: { original: BackendTypes.UserInvitation } }) => {
						const { email } = row.original;
						return (
							<TFText inherit lineClamp={1}>
								{email}
							</TFText>
						);
					},
				},
				{
					header: 'Capabilities',
					accessorKey: 'capabilities',
					size: 100,
					Cell: ({ row }: { row: { original: BackendTypes.UserInvitation } }) => {
						const { userParams } = row.original;
						return (
							<Flex>
								{userParams.listRoleCapabilities.map((cap) => (
									<Tooltip className={classes.icon} key={cap} label={capabilities[cap]}>
										{capabilitiesIcons[cap]}
									</Tooltip>
								))}
							</Flex>
						);
					},
				},
				{
					header: 'Revoke invitation',
					accessorKey: 'revoke',
					size: 100,
					enableSorting: false,
					enableResizing: false,
					Cell: ({ row }: { row: { original: BackendTypes.UserInvitation } }) => {
						const { email } = row.original;
						return (
							<Button
								disabled={
									revokeUserInvitationMutation.isPending &&
									revokeUserInvitationMutation.variables?.email === email
								}
								onClick={async () => {
									await revokeUserInvitationMutation.mutateAsync({ email });
								}}
								size={'xs'}
							>
								Revoke
							</Button>
						);
					},
				},
			]}
		/>
	);
};
